import React from "react";
import {
  getAllUsersDetails,
  getAllUsersMeta,
  getAllReferralCodes,
  getUserAgeFromMeta,
  getUserAnswerQuestionText,
  normalizeUserAnswerQuestions,
  normalizeReferralCode,
  softDeleteUser,
  hardDeleteUser,
  restoreUser,
} from "../utils";
import { useState, useEffect } from "react";
import DataTable from "../UsersDataTable";

export default function BoatOwners() {
  const [data, setData] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleSoftDelete = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this boat owner? This action can be undone by an admin.')) return;
    try {
      await softDeleteUser(userId);
      setData((prev) => prev.map((u) => u.userId === userId ? { ...u, isDeleted: true } : u));
    } catch (error) {
      console.error('Error deleting boat owner:', error);
    }
  };

  const handleHardDelete = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm('WARNING: This will PERMANENTLY delete this boat owner and all their data. This action CANNOT be undone. Are you sure?')) return;
    try {
      await hardDeleteUser(userId);
      setData((prev) => prev.filter((u) => u.userId !== userId));
    } catch (error) {
      console.error('Error permanently deleting boat owner:', error);
    }
  };

  const handleRestore = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to restore this boat owner?')) return;
    try {
      await restoreUser(userId);
      setData((prev) => prev.map((u) => u.userId === userId ? { ...u, isDeleted: false } : u));
    } catch (error) {
      console.error('Error restoring boat owner:', error);
    }
  };

  const columns = [
    {
      Header: "BBR ID",
      accessor: "bbrId",
    },
    {
      Header: "Full Name",
      accessor: "name",
    },
    {
      Header: "Phone",
      accessor: "phone",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Age",
      accessor: "age",
    },
    {
      Header: "Address Line 1",
      accessor: "address1",
    },
    {
      Header: "City",
      accessor: "city",
    },
    {
      Header: "State/Province",
      accessor: "state",
    },
    {
      Header: "Zip Code",
      accessor: "zipCode",
    },
    {
      Header: "Country",
      accessor: "country",
    },
    {
      Header: "Referral Code (Owner)",
      accessor: "ownerReferralCode",
    },
    {
      Header: "Referral Code by (Referral)",
      accessor: "referredByCode",
    },
    {
      Header: "Pro Staff Member",
      accessor: "isProStaff",
    },
    {
      Header: "Social Security Number",
      accessor: "Social Security Number",
    },
    {
      Header: "Federal ID",
      accessor: "Federal ID",
    },
    {
      Header: "Individual or Business Name",
      accessor: "Individual or Business Name",
    },
    {
      Header: "Action",
      accessor: "action",
      disableSortBy: true,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [allUsers, allUserMeta, referralCodes] = await Promise.all([
          getAllUsersDetails(),
          getAllUsersMeta(),
          getAllReferralCodes(),
        ]);

        const usersById = Object.fromEntries(
          allUsers.map((user) => [user.userId, user])
        );

        const userMetaByUserId = Object.fromEntries(
          allUserMeta.map((meta) => [meta.uid, meta])
        );

        const referralCodeByUserId = {};
        const userIdByReferralCode = {};
        referralCodes.forEach(({ userId, code }) => {
          if (!userId || !code) return;
          referralCodeByUserId[userId] = code;
          userIdByReferralCode[normalizeReferralCode(code)] = userId;
        });

        const boatOwners = allUsers
          .filter(
            (user) =>
              user.role === "Boat Owner" &&
              user.status === "Active" &&
              (showDeleted ? user.isDeleted : !user.isDeleted)
          )
          .map((user) => {
            try {
              const userMeta = userMetaByUserId[user.userId];
              const ownerReferralCode = referralCodeByUserId[user.userId] || null;

              let referredByCode = "-";
              if (user.refferalCode && user.refferalCode.trim() !== "") {
                const referrerUserId =
                  userIdByReferralCode[normalizeReferralCode(user.refferalCode)];
                if (referrerUserId && usersById[referrerUserId]) {
                  referredByCode = user.refferalCode;
                }
              }

              const getKeyValue = (colum) => {
                const textQuestions = normalizeUserAnswerQuestions(
                  userMeta?.userAnswers?.textQuestions
                );
                return textQuestions.find(
                  (q) =>
                    getUserAnswerQuestionText(q).toLowerCase() ===
                    colum?.toLowerCase()
                )?.answer;
              };

              return {
                ...user,
                age: getUserAgeFromMeta(userMeta),
                address1: user.address1 || getKeyValue("Address line 1"),
                city: user.city || getKeyValue("city"),
                state: user.state || getKeyValue("State/Province"),
                zipCode: user.zipCode || getKeyValue("Zip Code"),
                country: user.country || getKeyValue("Country") || "-",
                ownerReferralCode: ownerReferralCode || "-",
                referredByCode,
                isProStaff:
                  ownerReferralCode && ownerReferralCode !== "-" ? "Yes" : "No",
                ["Social Security Number"]: getKeyValue(
                  "Social Security Number (XXX-XX-XXXX)"
                ),
                ["Federal ID"]: getKeyValue("Federal ID (EIN) (XX-XXXXXXX)"),
                ["Individual or Business Name"]: getKeyValue(
                  "Individual or Business Name  (as shown on your income tax return)"
                ),
                name: `${user.firstName} ${user.lastName}`,
              };
            } catch (userError) {
              console.error(
                `Error processing boat owner ${user?.userId || "unknown"}:`,
                userError
              );
              return null;
            }
          })
          .filter(Boolean);

        setData(boatOwners);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showDeleted]);

  return (
    <>
      <div class="row">
        <div class="col">
          <div class="page-description">
            <h1>{showDeleted ? 'Deleted Boat Owners' : 'All Boat Owners'}</h1>
          </div>
        </div>
        <div class="col-auto">
          <button
            className={`btn ${showDeleted ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
            onClick={() => setShowDeleted(!showDeleted)}
          >
            {showDeleted ? 'Show Active Boat Owners' : 'Show Deleted Boat Owners'}
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="card calendar-container">
            <div class="card-body">
              {loading ? (
                <p className="text-muted mb-0">Loading boat owners...</p>
              ) : (
                <DataTable
                  columns={columns}
                  data={data}
                  onDelete={showDeleted ? handleHardDelete : handleSoftDelete}
                  onRestore={showDeleted ? handleRestore : null}
                  deleteMode={showDeleted ? 'permanent' : 'soft'}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
